/**
 * IncomeVsExpensesChart - Grouped bar chart comparing income and expenses over time
 *
 * Features:
 * - Grouped bar chart with Income (green) and Expenses (red) bars
 * - Summary showing total income, total expenses, net
 * - Net is color-coded (green if positive, red if negative)
 * - Loading skeleton state
 * - Empty state when no data
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Loader2, BarChart3 } from 'lucide-react';
import type { IncomeExpensesReportResponse } from '@/types/accounting';

interface IncomeVsExpensesChartProps {
  /** Income vs expenses report data from API */
  data: IncomeExpensesReportResponse | null;
  /** Whether data is currently loading */
  loading: boolean;
}

/**
 * Format cents to currency display using EUR
 */
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

/**
 * Format compact number for Y-axis labels
 */
function formatCompact(value: number): string {
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return value.toString();
}

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse" data-testid="income-expenses-chart-skeleton">
      <div className="flex justify-between items-start mb-6">
        <div className="h-5 bg-gray-200 rounded w-48" />
        <div className="flex gap-6">
          <div className="text-right">
            <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
            <div className="h-6 bg-gray-200 rounded w-24" />
          </div>
          <div className="text-right">
            <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
            <div className="h-6 bg-gray-200 rounded w-24" />
          </div>
          <div className="text-right">
            <div className="h-3 bg-gray-200 rounded w-12 mb-2" />
            <div className="h-6 bg-gray-200 rounded w-24" />
          </div>
        </div>
      </div>
      <div className="h-80 bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-gray-400 flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading chart data...
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state when no income/expense data is available
 */
function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="income-expenses-chart-empty">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Income vs Expenses
      </h3>
      <div className="py-12 text-center">
        <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-gray-700 mb-2">
          No Income/Expense Data
        </h4>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          No transactions found for the selected date range.
          Try adjusting your filters or importing some transactions.
        </p>
      </div>
    </div>
  );
}

export function IncomeVsExpensesChart({ data, loading }: IncomeVsExpensesChartProps) {
  // Show skeleton while loading
  if (loading && !data) {
    return <ChartSkeleton />;
  }

  // Show empty state if no data or no periods
  if (!data || data.periods.length === 0) {
    return <EmptyState />;
  }

  const chartData = data.periods.map((d) => ({
    period: d.period,
    income: d.incomeCents / 100,
    expenses: d.expensesCents / 100,
    net: d.netCents / 100,
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="income-expenses-chart">
      <div className="flex justify-between items-start mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Income vs Expenses
          {loading && (
            <Loader2 className="w-4 h-4 ml-2 inline-block animate-spin text-gray-400" />
          )}
        </h3>
        <div className="flex gap-6">
          <div className="text-right">
            <div className="text-sm text-gray-500">Total Income</div>
            <div className="text-lg font-semibold text-green-600" data-testid="total-income">
              {formatCurrency(data.totalIncomeCents)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Total Expenses</div>
            <div className="text-lg font-semibold text-red-600" data-testid="total-expenses">
              {formatCurrency(data.totalExpensesCents)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Net</div>
            <div
              className={`text-lg font-semibold ${
                data.totalNetCents >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
              data-testid="total-net"
            >
              {formatCurrency(data.totalNetCents)}
            </div>
          </div>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="period"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
              tickFormatter={formatCompact}
            />
            <Tooltip
              formatter={(value) => formatCurrency((value as number) * 100)}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
              iconSize={10}
            />
            <ReferenceLine y={0} stroke="#9CA3AF" />
            <Bar
              dataKey="income"
              name="Income"
              fill="#10B981"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="expenses"
              name="Expenses"
              fill="#EF4444"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
